// ─────────────────────────────────────────────────────────────────────────────
// Login.js
// Contractor login and signup page.
//
// ADMIN BLOCK: After sign in, checks if the user is a superadmin.
// If is_superadmin = true, signs them out and shows an error — admin
// accounts must use /admin/login instead.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react'
import { supabase } from '../supabaseClient'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isSignUp, setIsSignUp] = useState(false)
  const [message, setMessage] = useState(null)

  const toggleMode = () => {
    setIsSignUp(!isSignUp)
    setError(null)
    setMessage(null)
    setCompanyName('')
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setMessage(null)

    if (isSignUp) {
      if (!companyName.trim()) {
        setError('Please enter your company name.')
        setLoading(false)
        return
      }

      const { data, error: signUpError } = await supabase.auth.signUp({ email, password })
      if (signUpError) { setError(signUpError.message); setLoading(false); return }

      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert([{
          company_name: companyName.trim(),
          next_quote_number: 1,
          trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          plan_status: 'trial',
        }])
        .select()
        .single()

      if (companyError) { setError(companyError.message); setLoading(false); return }

      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{ id: data.user.id, full_name: '', company_id: company.id, role: 'admin' }])

      if (profileError) { setError(profileError.message); setLoading(false); return }

      setMessage('Account created! Please check your email to confirm.')

    } else {
      // ── Sign in ──────────────────────────────────────────────────────────
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password })
      if (signInError) { setError(signInError.message); setLoading(false); return }

      // ── Block superadmin accounts from contractor login ──────────────────
      // Admin accounts must use /admin/login
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_superadmin')
        .eq('id', data.user.id)
        .maybeSingle()

      if (profile?.is_superadmin === true) {
        await supabase.auth.signOut()
        setError('This account is for admin use only. Please use the admin login page.')
        setLoading(false)
        return
      }
    }

    setLoading(false)
  }

  const inputStyle = {
    width: '100%', height: '44px', borderRadius: '10px',
    border: '1.5px solid #E2E8F0', padding: '0 14px',
    fontSize: '14px', color: '#0F172A', outline: 'none',
    boxSizing: 'border-box', background: '#FAFAFA',
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#F7F8FC',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── Nav ── */}
      <nav style={{
        padding: '0 48px', height: '60px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'white', borderBottom: '1px solid #EAECF0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: '800', color: 'white', fontSize: '15px',
          }}>Q</div>
          <span style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A' }}>QuoteFlow</span>
        </div>
        <span style={{ fontSize: '13px', color: '#64748B' }}>
          {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
          <span onClick={toggleMode} style={{ color: '#2563EB', fontWeight: '600', cursor: 'pointer' }}>
            {isSignUp ? 'Sign in' : 'Sign up free'}
          </span>
        </span>
      </nav>

      {/* ── Body ── */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '48px 24px',
      }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>

          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: '999px', padding: '4px 12px',
            fontSize: '12px', fontWeight: '600', color: '#2563EB', marginBottom: '20px',
          }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2563EB', display: 'inline-block' }} />
            {isSignUp ? 'Create your free account' : 'Welcome back'}
          </div>

          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0F172A', margin: '0 0 8px', letterSpacing: '-0.5px' }}>
            {isSignUp ? 'Start managing your quotes' : 'Sign in to QuoteFlow'}
          </h1>
          <p style={{ fontSize: '15px', color: '#64748B', margin: '0 0 28px' }}>
            {isSignUp
              ? 'Create your account and set up your company in seconds.'
              : 'Enter your credentials to access your dashboard.'}
          </p>

          {/* Card */}
          <div style={{
            background: 'white', borderRadius: '16px',
            border: '1px solid #EAECF0', padding: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
          }}>
            {error && (
              <div style={{
                background: '#FFF1F2', border: '1px solid #FECDD3',
                borderRadius: '10px', padding: '12px 16px',
                color: '#BE123C', fontSize: '13px', marginBottom: '20px',
              }}>{error}</div>
            )}
            {message && (
              <div style={{
                background: '#F0FDF4', border: '1px solid #BBF7D0',
                borderRadius: '10px', padding: '12px 16px',
                color: '#15803D', fontSize: '13px', marginBottom: '20px',
              }}>{message}</div>
            )}

            {/* Company name — sign up only */}
            {isSignUp && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Company Name
                </label>
                <input type="text" value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g. ABC Plumbing Ltd" style={inputStyle} />
              </div>
            )}

            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Email address
              </label>
              <input type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com" style={inputStyle} />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Password
              </label>
              <input type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={inputStyle} />
            </div>

            <button onClick={handleSubmit} disabled={loading} style={{
              width: '100%', height: '46px', borderRadius: '10px',
              background: loading ? '#93C5FD' : 'linear-gradient(135deg, #2563EB, #1D4ED8)',
              color: 'white', fontSize: '14px', fontWeight: '700',
              border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 2px 8px rgba(37,99,235,0.35)',
              marginBottom: '20px',
            }}>
              {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>

            <p style={{ textAlign: 'center', fontSize: '13px', color: '#64748B', margin: 0 }}>
              {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
              <span onClick={toggleMode} style={{ color: '#2563EB', fontWeight: '700', cursor: 'pointer' }}>
                {isSignUp ? 'Sign in' : 'Sign up free'}
              </span>
            </p>
          </div>

          {/* Trust row */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginTop: '24px' }}>
            {['Secure and encrypted', 'GDPR compliant', 'Free to start'].map((t, i) => (
              <span key={i} style={{ fontSize: '11px', color: '#94A3B8', fontWeight: '500' }}>{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}